import type {
	LegacyButton,
	LegacyHeader,
	LogoBox,
	MenuItem,
	TextUrl,
} from '@bw/apis/contentful';
import { slugify } from '@bw/core';
import { AgencyDetails, AgencyInsuranceType } from '@bw/external-apis/shared';
import { Asset, AssetSys } from 'contentful';
import { Brightway_Logo, man } from '../../../stock-images';

const byInsuranceTypeSortOrder = (
	a: AgencyInsuranceType,
	b: AgencyInsuranceType
) => {
	return a.insuranceTypeSortOrder - b.insuranceTypeSortOrder;
};

type ContentTypeMap = {
	legacyHeader: LegacyHeader;
	logoBox: LogoBox;
	menuItem: MenuItem;
	textUrl: TextUrl;
	legacyButton: LegacyButton;
};

const entry = <K extends keyof ContentTypeMap>(
	id: K,
	fields: ContentTypeMap[K]['fields']
): ContentTypeMap[K] =>
	({
		sys: {
			contentType: { sys: { id, type: 'Link', linkType: 'ContentType' } },
		} as ContentTypeMap[K]['sys'],
		metadata: { tags: [], concepts: [] },
		fields: fields as unknown as ContentTypeMap[K]['fields'],
	}) as ContentTypeMap[K];

const asset = (
	fields: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']
): Asset<'WITHOUT_UNRESOLVABLE_LINKS'> => ({
	metadata: { tags: [], concepts: [] },
	sys: {
		type: 'Asset',
	} as AssetSys,
	fields,
});

export const buildHeaderData = (
	agencyDetails: AgencyDetails,
	stateAbbrForUrl: string,
	loginUrl: string,
	lockupImage?: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>
): LegacyHeader => {
	const logoImage = lockupImage ?? asset({
		title: 'Brightway Insurance',
		file: {
			url: Brightway_Logo,
			fileName: 'Brightway_Logo',
			contentType: 'image/png',
			details: { size: 0, image: { width: 167, height: 32 } },
		},
	});

	return entry('legacyHeader', {
		name: 'Main Header',
		title: 'Main Header',
		containsBookACallButton: false,
		containsStartAQuoteButton: true,
		logo: entry('logoBox', {
			title: 'Brightway Insurance',
			imageUrl: `/agencies/${stateAbbrForUrl}/${slugify(
				agencyDetails.agency.city
			)}/${agencyDetails.agency.storeNumber}`,
			altText: 'Brightway Insurance',
			image: logoImage,
		}),
		menuItems: [
			entry('menuItem', {
				title: 'Find Insurance',
				url: '/insurance',
				hideMenuActionSection: false,
				subMenu: [
					...agencyDetails.agency.agencyInsuranceTypes
						.sort(byInsuranceTypeSortOrder)
						.map((type) =>
							entry('textUrl', {
								text: type.insuranceTypeName,
								url: `/agencies/${stateAbbrForUrl}/${slugify(
									agencyDetails.agency.alias
								)}/${agencyDetails.agency.storeNumber
									}/services/${type.insuranceTypeAlias.toLowerCase()}`,
							})
						),
				],
			}),
			entry('textUrl', {
				text: 'About Us',
				containsBookACallFunctionality: false,
				url: `/agencies/${stateAbbrForUrl}/${slugify(
					agencyDetails.agency.city
				)}/${agencyDetails.agency.storeNumber}/team`,
			}),
			entry('menuItem', {
				title: 'Support',
				url: '/support',
				menuBannerImage: asset({
					title: 'Other Insurance',
					file: {
						url: man,
						details: {
							image: {
								width: 982,
								height: 1360,
							},
							size: 0,
						},
						fileName: '',
						contentType: '',
					},
				}),
				subMenu: [
					entry('textUrl', {
						text: 'Customer Service',
						url: `/agencies/${stateAbbrForUrl}/${slugify(
							agencyDetails.agency.city
						)}/${agencyDetails.agency.storeNumber}/customer-service`,
					}),
					entry('textUrl', {
						text: 'Contact Us',
						url: `/agencies/${stateAbbrForUrl}/${slugify(
							agencyDetails.agency.city
						)}/${agencyDetails.agency.storeNumber}/contact-us`,
					}),
					entry('textUrl', {
						text: 'Log In',
						url: loginUrl,
					}),
				],
			}),
		],

		ctaLinks: [
			entry('legacyButton', {
				title: agencyDetails.agency.phone,
				buttonUrl: 'tel:' + agencyDetails.agency.phone,
				textColour: 'white',
				backgroundColour: 'deepBlue',
				borderColour: 'none',
				type: 'button',
			}),
			entry('legacyButton', {
				title: 'Log In',
				buttonUrl: loginUrl,
				textColour: 'white',
				backgroundColour: 'deepBlue',
				borderColour: 'none',
				type: 'button',
			}),
		],
		actionLinks: [
			entry('textUrl', {
				text: 'Make a payment',
				url: '/insurance/insurance-companies',
			}),
			entry('textUrl', {
				text: 'Report a claim',
				url: '/insurance/insurance-companies',
			}),
		],
	});
};
